import React, { FC } from 'react';

import Typography from '@material-ui/core/Typography';
import { fade } from '@material-ui/core/styles';
import styled from '@material-ui/core/styles/styled';
import { Link } from 'react-router-dom';

import { IKImage } from '../../../../components/IKImage';
import { PathwayLogo } from '../../../../components/TestResults';
import { colorsPalette } from '../../../../theme';
import { COURSE_STATUSES } from '../../../../types';
import ProgressBar from '../ProgressBar';
import CourseTags from '../TagsList/TagsList';
import { useStyles } from './styles';

interface IMetaInfo {
  pathways?: string | null;
  learning_hours?: string | null;
  experts?: string | null;
  cpd_credits?: string | null;
}

interface CourseCardProps {
  circleImageUrl?: string | null;
  logoSvg?: string | null;
  image: string;
  title?: string | null;
  description?: string | null;
  link: string;
  meta?: IMetaInfo | null;
  progress?: number;
  status?: COURSE_STATUSES | null;
}

const CourseCard: FC<CourseCardProps> = ({
  circleImageUrl,
  logoSvg,
  image,
  title,
  description,
  link,
  meta,
  progress,
  status,
}) => {
  const classes = useStyles();

  return (
    <Card to={link}>
      <PathwayLogo
        wrapperClass={classes.cardIcon}
        circleUrl={circleImageUrl}
        svgContent={logoSvg}
      />
      <Content>
        <Title variant="h2">{title}</Title>
        <CardDesc>{description}</CardDesc>
        <Divider />
        <CourseTags
          CPD={meta?.cpd_credits}
          pathways={meta?.pathways}
          experts={meta?.experts}
          hours={meta?.learning_hours}
        />
      </Content>
      <Background>
        <IKImage
          className={classes.cardImage}
          path={image}
          srcset={[{ width: 500 }]}
          sizes="500px"
        />
      </Background>
      <ProgressBar progress={progress} status={status} />
    </Card>
  );
};

export default CourseCard;

const Card = styled(Link)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minHeight: 420,
  marginLeft: 9,
  boxSizing: 'border-box',
  borderRadius: 5,
  boxShadow: `0px 3px 6px ${fade(colorsPalette.black, 0.4)}`,
  color: colorsPalette.white,
  background: colorsPalette.blackSecondary,
  overflow: 'hidden',
  '&:nth-child(odd)': {
    marginRight: 9,
    marginLeft: 0,
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: 8,
  },
}));
const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 8,
  fontSize: 20,
  [theme.breakpoints.up('md')]: {
    fontSize: 28,
  },
}));
const Background = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  maxHeight: 230,
  background: colorsPalette.blackSecondary,
  '&:before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '35%',
    height: '100%',
    background: `linear-gradient(90deg, ${fade(colorsPalette.blueDark, 0.6)} 0%, ${fade(
      colorsPalette.blueDark,
      0,
    )} 99.05%)`,
  },
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: 'calc(100% - 70px)',
    background: `linear-gradient(0deg, ${colorsPalette.blackSecondary} 0%, rgba(36, 34, 47, 0.901961) 17.24%, rgba(45, 50, 56, 0.0001) 100%)`,
  },
  [theme.breakpoints.up('md')]: {
    height: 'calc(100% - 170px)',
  },
}));
const Content = styled('div')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  marginTop: 'auto',
  padding: '36px 16px 8px',
  [theme.breakpoints.up('md')]: {
    padding: '36px 24px 8px',
  },
}));
const CardDesc = styled(Typography)(({ theme }) => ({
  display: '-webkit-box',
  maxHeight: 96,
  marginBottom: 24,
  color: colorsPalette.gray,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '-webkit-line-clamp': 4,
  '-webkit-box-orient': 'vertical',
  [theme.breakpoints.up('md')]: {
    fontSize: 18,
    maxHeight: 80,
    '-webkit-line-clamp': 3,
  },
}));
const Divider = styled('div')({
  width: '100%',
  height: 1,
  marginBottom: 24,
  background: fade(colorsPalette.white, 0.3),
});
